import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FEATURE_FLAGS } from '~common';
import { useQueryGetStationInformation } from '~hooks/station/useQueryGetStationInformation';
import { useQueryGetStationStatusByImeiOrSerial } from '~hooks/station/useQueryGetStationStatusByImeiOrSerial';
import {
  ConnectionState,
  StationBatteryAvailability,
  StationCampusCard,
} from '~types/station.type';
import { checkEnabledFeatureFlag } from '~utils';

type StationInformationContextType = {
  stationOnlineStatus?: boolean;
  connectionState?: ConnectionState;
  batteryAvailability?: StationBatteryAvailability;
  isFetchingStationInformation: boolean;
  isFetchedStationInformation: boolean;
  campusCard: StationCampusCard;
};

const DEFAULT_CAMPUS_CARD: StationCampusCard = { enabled: false };

const StationInformationContext = createContext<StationInformationContextType>({
  stationOnlineStatus: false,
  connectionState: undefined,
  batteryAvailability: undefined,
  isFetchingStationInformation: false,
  isFetchedStationInformation: false,
  campusCard: DEFAULT_CAMPUS_CARD,
});

export const StationInformationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const stationImeiOrSerialNumber = searchParams.get('box');

  const isCampusCardEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.CAMPUS_CARD_BY_WEB_PLATFORM,
  );

  const query = useQueryGetStationInformation(
    { box: stationImeiOrSerialNumber || '' },
    { enabled: !!stationImeiOrSerialNumber && isCampusCardEnabled },
  );

  const legacyQuery = useQueryGetStationStatusByImeiOrSerial(
    { box: stationImeiOrSerialNumber || '' },
    { enabled: !!stationImeiOrSerialNumber && !isCampusCardEnabled },
  );

  const providerValues = useMemo(() => {
    if (isCampusCardEnabled) {
      return {
        stationOnlineStatus: query.data?.online,
        connectionState: query.data?.connectionState,
        batteryAvailability: query.data?.availability,
        isFetchingStationInformation: query.isFetching,
        isFetchedStationInformation: query.isFetched,
        campusCard: query.data?.campusCard ?? DEFAULT_CAMPUS_CARD,
      };
    }
    return {
      stationOnlineStatus: legacyQuery.data?.online,
      connectionState: legacyQuery.data?.connectionState,
      batteryAvailability: legacyQuery.data?.availability,
      isFetchingStationInformation: legacyQuery.isFetching,
      isFetchedStationInformation: legacyQuery.isFetched,
      campusCard: DEFAULT_CAMPUS_CARD,
    };
  }, [
    isCampusCardEnabled,
    query.data,
    query.isFetching,
    query.isFetched,
    legacyQuery.data,
    legacyQuery.isFetching,
    legacyQuery.isFetched,
  ]);

  return (
    <StationInformationContext.Provider value={providerValues}>
      {children}
    </StationInformationContext.Provider>
  );
};

export const useStationInformationContext = () =>
  useContext(StationInformationContext);
